import { useState, useEffect } from 'react';
import { X, FileText, AlertTriangle } from 'lucide-react';
import { notasApi } from '../api/notas';

function toDateInputValue(d) {
  if (!d) return '';
  return new Date(d).toISOString().slice(0, 10);
}

function formatCurrency(n) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);
}

export function ModalGenerarRelacion({ isOpen, onClose, onSaved, clienteId, notas = [] }) {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [empleadoId, setEmpleadoId] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && notas.length > 0) {
      // Por defecto: rango que cubre todas las notas disponibles
      const min = Math.min(...notas.map((n) => new Date(n.fecha).getTime()));
      const max = Math.max(...notas.map((n) => new Date(n.fecha).getTime()));
      setDesde(toDateInputValue(new Date(min)));
      setHasta(toDateInputValue(new Date(max)));
    } else {
      setDesde('');
      setHasta('');
      setPreview(null);
    }
  }, [isOpen, notas]);

  useEffect(() => {
    if (!isOpen) {
      setEmpleadoId('');
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const empleadosDisponibles = [...new Map(notas.map((n) => [n.empleadoId, n.empleadoNombre])).entries()]
    .filter(([id, name]) => id != null)
    .map(([id, name]) => ({ id, nombre: name }));

  const handlePreview = async () => {
    if (!desde || !hasta) {
      setError('Selecciona rango de fechas (desde y hasta).');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await notasApi.previewRelacion(clienteId, {
        desde: new Date(desde).toISOString(),
        hasta: new Date(hasta + 'T23:59:59').toISOString(),
        empleadoId: empleadoId ? Number(empleadoId) : null,
      });
      setPreview(data);
    } catch (e) {
      setError(e?.response?.data?.error || 'Error al obtener preview');
    } finally {
      setLoading(false);
    }
  };

  const handleCrear = async () => {
    if (!preview) {
      setError('Genera el preview primero.');
      return;
    }
    if (preview.itemsCount === 0) {
      setError('No hay notas disponibles en el rango.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await notasApi.crearRelacion(clienteId, {
        desde: new Date(desde).toISOString(),
        hasta: new Date(hasta + 'T23:59:59').toISOString(),
        empleadoId: empleadoId ? Number(empleadoId) : null,
      });
      const relId = result?.id ?? result;
      onSaved?.(relId);
    } catch (e) {
      setError(e?.response?.data?.error || 'Error al crear la relación');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-panel border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="text-lg font-bold text-white">Generar relación desde notas</h2>
            <p className="text-xs text-muted mt-0.5">
              {preview
                ? `${preview.itemsCount} nota(s) seleccionadas`
                : `${notas.length} nota(s) disponibles en total`}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded text-slate-400 hover:text-white hover:bg-panel-2">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5 tracking-wide">Desde</label>
              <input
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5 tracking-wide">Hasta</label>
              <input
                type="date"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
                className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5 tracking-wide">Empleado (opcional)</label>
              <select
                value={empleadoId}
                onChange={(e) => setEmpleadoId(e.target.value)}
                className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white"
              >
                <option value="">(todos)</option>
                {empleadosDisponibles.map((e) => (
                  <option key={e.id} value={e.id}>{e.nombre}</option>
                ))}
              </select>
            </div>
          </div>

          <button
            onClick={handlePreview}
            disabled={loading}
            className="w-full px-4 py-2 rounded bg-panel-2 hover:bg-border text-slate-200 text-sm font-semibold disabled:opacity-50"
          >
            {loading ? 'Calculando...' : 'Vista previa'}
          </button>

          {preview && preview.itemsCount === 0 && (
            <div className="text-sm text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2 flex items-center gap-2">
              <AlertTriangle size={14} />
              No hay notas en este rango. Ajusta las fechas o el empleado.
            </div>
          )}

          {preview && preview.itemsCount > 0 && (
            <>
              <div className="bg-panel-2 border border-border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-bg/50 text-muted">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Fecha</th>
                      <th className="text-left px-3 py-2 font-medium">Servicio</th>
                      <th className="text-left px-3 py-2 font-medium">Empleado</th>
                      <th className="text-right px-3 py-2 font-medium">Importe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.items.slice(0, 8).map((it, i) => (
                      <tr key={i} className="border-t border-border/40">
                        <td className="px-3 py-1.5 text-slate-400 text-[10px]">
                          {new Date(it.fecha).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                        </td>
                        <td className="px-3 py-1.5 text-slate-300">{it.servicioNombre}</td>
                        <td className="px-3 py-1.5 text-slate-400 text-[10px]">{it.empleadoNombre || '—'}</td>
                        <td className="px-3 py-1.5 text-right text-emerald-400">
                          {formatCurrency(it.importeEfectivo)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.itemsCount > 8 && (
                  <div className="text-[10px] text-muted text-center py-1 border-t border-border/40">
                    …y {preview.itemsCount - 8} más
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-panel-2 border border-border rounded p-2">
                  <div className="text-muted">Subtotal</div>
                  <div className="text-slate-300 font-mono">{formatCurrency(preview.subtotal)}</div>
                </div>
                <div className="bg-panel-2 border border-border rounded p-2">
                  <div className="text-muted">IVA (16%)</div>
                  <div className="text-slate-300 font-mono">{formatCurrency(preview.iva)}</div>
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-2">
                  <div className="text-muted">Total</div>
                  <div className="text-emerald-400 font-mono font-bold">{formatCurrency(preview.total)}</div>
                </div>
              </div>
            </>
          )}

          {error && (
            <div className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded border border-border text-slate-300 hover:bg-panel-2 text-sm"
          >
            Cancelar
          </button>
          <button
            onClick={handleCrear}
            disabled={!preview || preview.itemsCount === 0 || saving}
            className="flex items-center gap-2 px-4 py-2 rounded bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50"
          >
            <FileText size={14} /> {saving ? 'Creando...' : 'Crear relación'}
          </button>
        </div>
      </div>
    </div>
  );
}
